/**
 * Smart Suggestions — Sprint 3
 * Shows: remaining macro budget, "same as yesterday" quick-copy, frequent foods carousel
 */

import { useState } from 'react';
import { Copy, Flame, Zap, TrendingDown, ChevronLeft, ChevronRight, Utensils } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useSmartSuggestions, type YesterdayMeal, type SmartFoodSuggestion } from '@/hooks/use-smart-suggestions';
import { useNutritionLogs } from '@/hooks/use-nutrition-logs';
import { MEAL_TYPE_LABELS } from '@/lib/nutrition/constants';
import type { MealType } from '@/lib/nutrition';
import { format } from 'date-fns';

interface SmartSuggestionsProps {
  clientId: string;
}

export function SmartSuggestions({ clientId }: SmartSuggestionsProps) {
  const { budget, hasGoal, yesterdayMeals, quickLogFoods, isLoading } = useSmartSuggestions(clientId);
  const { createLog } = useNutritionLogs({ clientId });
  const { toast } = useToast();
  const [carouselOffset, setCarouselOffset] = useState(0);

  const handleCopyYesterday = async (meal: YesterdayMeal) => {
    try {
      await createLog.mutateAsync({
        meal_type: meal.meal_type,
        food_description: meal.food_description,
        calories: meal.calories,
        protein_g: meal.protein_g,
        carbs_g: meal.carbs_g,
        fat_g: meal.fat_g,
        logged_date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast({
        title: 'Meal copied',
        description: `${meal.food_description} added to today's ${MEAL_TYPE_LABELS[meal.meal_type]}.`,
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Could not copy meal. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleQuickLog = async (suggestion: SmartFoodSuggestion) => {
    try {
      await createLog.mutateAsync({
        meal_type: 'snack' as MealType,
        food_description: suggestion.food.name,
        calories: suggestion.food.calories ?? null,
        protein_g: suggestion.food.protein ?? null,
        carbs_g: suggestion.food.carbs ?? null,
        fat_g: suggestion.food.fat ?? null,
        logged_date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast({
        title: 'Food logged',
        description: `${suggestion.food.name} added.`,
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Could not log food. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const showBudget = hasGoal && budget;
  const showYesterday = yesterdayMeals.length > 0;
  const showQuickLog = quickLogFoods.length > 0;

  if (!showBudget && !showYesterday && !showQuickLog) {
    return null; // Nothing to suggest
  }

  const visibleFoods = quickLogFoods.slice(carouselOffset, carouselOffset + 4);
  const canScrollLeft = carouselOffset > 0;
  const canScrollRight = carouselOffset + 4 < quickLogFoods.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4 text-accent" />
          Smart Suggestions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Remaining Macro Budget */}
        {showBudget && (
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Remaining today</p>
            <div className="grid grid-cols-4 gap-2">
              <BudgetPill label="Cal" value={budget?.calories?.remaining ?? 0} color="text-foreground" />
              <BudgetPill label="Protein" value={budget?.protein_g?.remaining ?? 0} unit="g" color="text-destructive" />
              <BudgetPill label="Carbs" value={budget?.carbs_g?.remaining ?? 0} unit="g" color="text-info" />
              <BudgetPill label="Fat" value={budget?.fat_g?.remaining ?? 0} unit="g" color="text-warning" />
            </div>
          </div>
        )}

        {/* Same as Yesterday */}
        {showYesterday && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Same as yesterday</p>
            <div className="space-y-1.5">
              {yesterdayMeals.slice(0, 4).map((meal) => (
                <button
                  key={meal.log_id}
                  onClick={() => handleCopyYesterday(meal)}
                  disabled={createLog.isPending}
                  className="w-full flex items-center gap-3 rounded-lg border border-border/50 bg-card p-2.5 text-left hover:bg-muted/50 transition-colors cursor-pointer focus-ring group"
                >
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Utensils className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{meal.food_description}</p>
                    <p className="text-xs text-muted-foreground">
                      {MEAL_TYPE_LABELS[meal.meal_type]}
                      {meal.calories ? ` · ${meal.calories} cal` : ''}
                    </p>
                  </div>
                  <Copy className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-foreground transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick-Log Carousel */}
        {showQuickLog && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">Your frequent foods</p>
              {quickLogFoods.length > 4 && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={!canScrollLeft}
                    onClick={() => setCarouselOffset(Math.max(0, carouselOffset - 4))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={!canScrollRight}
                    onClick={() => setCarouselOffset(carouselOffset + 4)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {visibleFoods.map((suggestion, i) => (
                <button
                  key={`${suggestion.food.id}-${i}`}
                  onClick={() => handleQuickLog(suggestion)}
                  disabled={createLog.isPending}
                  className="flex flex-col items-start rounded-lg border border-border/50 bg-card p-2.5 text-left hover:bg-muted/50 transition-colors cursor-pointer focus-ring"
                >
                  <p className="text-xs font-medium truncate w-full">{suggestion.food.name}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {suggestion.food.calories != null && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <Flame className="h-2.5 w-2.5" />
                        {suggestion.food.calories}
                      </span>
                    )}
                    {suggestion.fitsCalorieBudget && hasGoal && (
                      <Badge variant="outline" className="h-4 text-[9px] px-1 border-accent/30 text-accent">
                        <TrendingDown className="h-2 w-2 mr-0.5" />
                        fits
                      </Badge>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground/60 mt-0.5">
                    used {suggestion.useCount}x
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BudgetPill({ label, value, unit = '', color }: { label: string; value: number; unit?: string; color: string }) {
  return (
    <div className="text-center">
      <p className={`text-sm font-bold ${color} data-nums`}>{Math.round(value)}{unit}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
